import * as React from 'react';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useAppContext } from '@coverright/shared/contexts';
import { keycloak } from '@coverright/shared/keycloak';
import { useMedicareQuote } from './api/medicare/use-medicare-quote';
import {
  SectionCredits,
  ScoreTier,
  calcSectionCredits,
  calcDerivedScores,
  ScoringInput,
} from './scoring-logic';
import { logEvent } from '@coverright/shared/analytics';

// ─── Public types ─────────────────────────────────────────────────────────────

export type { SectionCredits, ScoreTier };

export interface ScoringContextValue {
  /** Per-section completion (0–100 each). */
  sectionCredits: SectionCredits;

  /**
   * Match Score (0–100).
   * Weights: Profile 18 · Doctors 28 · Meds 24 · Pharmacy 5 · Coverage 10 · Priorities 10 · Booking 5
   */
  matchScore: number;

  /**
   * Progress Score (0–100).
   * Weights: Profile 15 · Doctors 23 · Meds 19 · Pharmacy 5 · Coverage 8 · Priorities 10 · Booking 20
   */
  progressScore: number;

  /** Current tier based on matchScore thresholds. */
  tier: ScoreTier;

  /** True when matchScore >= 70 — AI recommendations are visible. */
  aiGateOpen: boolean;
}

// ─── Context setup ────────────────────────────────────────────────────────────

const DEFAULT_CREDITS: SectionCredits = {
  profile: 0, doctors: 0, meds: 0,
  pharmacy: 0, coverage: 0, priorities: 0, booking: 0,
};

const DEFAULT_VALUE: ScoringContextValue = {
  sectionCredits: DEFAULT_CREDITS,
  matchScore:     0,
  progressScore:  0,
  tier:           'none',
  aiGateOpen:     false,
};

export const ScoringContext = createContext<ScoringContextValue>(DEFAULT_VALUE);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ScoringProvider = ({ children }: { children: React.ReactNode }) => {
  const [appState] = useAppContext();
  const quote      = useMedicareQuote();

  const input = useMemo<ScoringInput>(() => ({
    situation:         appState.situation,
    hasDoctors:        appState.hasDoctors,
    hasDrugs:          appState.hasDrugs,
    benefits:          appState.benefits,
    paymentPreference: appState.paymentPreference,
    planType:          appState.planType,
    callScheduled:     appState.callScheduled,
    scoring:           appState.scoring,
    zip:               quote?.zip,
    county:            quote?.county,
    doctorCount:       quote?.preferredDoctors?.length  ?? 0,
    drugCount:         quote?.preferredDrugs?.length    ?? 0,
    pharmacyCount:     quote?.preferredPharmacies?.length ?? 0,
    coverageCount:     (quote?.coverage as string[] | null)?.length ?? 0,
  }), [
    appState.situation, appState.hasDoctors, appState.hasDrugs,
    appState.benefits, appState.paymentPreference, appState.planType,
    appState.callScheduled,
    // Track individual scoring flags rather than the object reference —
    // prevents stale memos if the object is reused instead of replaced.
    appState.scoring?.doctorsSkipped,
    appState.scoring?.doctorsReconfirmed,
    appState.scoring?.drugsSkipped,
    appState.scoring?.drugsReconfirmed,
    appState.scoring?.pharmacySelected,
    appState.scoring?.coverageAnswered,
    quote?.zip, quote?.county,
    quote?.preferredDoctors?.length,
    quote?.preferredDrugs?.length,
    quote?.preferredPharmacies?.length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    (quote?.coverage as string[] | null)?.length,
  ]);

  const value = useMemo<ScoringContextValue>(() => {
    const sectionCredits = calcSectionCredits(input);
    const derived        = calcDerivedScores(sectionCredits);
    return { sectionCredits, ...derived };
  }, [input]);

  const prevMatchScore = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (keycloak.authenticated && prevMatchScore.current !== value.matchScore) {
      logEvent('match_score_changed', { matchScore: value.matchScore });
      prevMatchScore.current = value.matchScore;
    }
  }, [value.matchScore]);

  return (
    <ScoringContext.Provider value={value}>
      {children}
    </ScoringContext.Provider>
  );
};

// ─── Consumer hook ────────────────────────────────────────────────────────────

export const useScoring = () => useContext(ScoringContext);
